/*
  Worker Tree PDF Export — original layout, TADABER navy palette.
  Adds a horizontal "FULLY PAID" stamp when the contract is fully settled.
*/

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const TADABER_NAVY = [15, 23, 42];       // slate-900 (replaces previous orange)
const SLATE_900 = [15, 23, 42];          // #0F172A
const SLATE_500 = [100, 116, 139];       // #64748B
const SLATE_700 = [51, 65, 85];          // #334155
const EMERALD = [16, 185, 129];          // #10B981
const EMERALD_DARK = [22, 163, 74];      // stamp green
const RED = [239, 68, 68];               // #EF4444

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    const date = typeof d === 'string' && /^\d{2}\/\d{2}\/\d{4}/.test(d)
      ? new Date(d.split(' ')[0].split('/').reverse().join('-'))
      : new Date(d);
    if (isNaN(date.getTime())) return d;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
};

const fmtMoney = (n) => `AED ${Number(n || 0).toLocaleString()}`;

const normalizePhoto = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  if (raw.startsWith('data:image')) return raw;
  return `data:image/jpeg;base64,${raw}`;
};

const upscalePhoto = (dataUrl, targetSize = 800) => new Promise((resolve) => {
  if (!dataUrl) return resolve(null);
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = targetSize;
      canvas.height = targetSize;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      const minSide = Math.min(img.width, img.height);
      const sx = (img.width - minSide) / 2;
      const sy = (img.height - minSide) / 2;
      ctx.drawImage(img, sx, sy, minSide, minSide, 0, 0, targetSize, targetSize);
      resolve(canvas.toDataURL('image/jpeg', 0.95));
    } catch {
      resolve(dataUrl);
    }
  };
  img.onerror = () => resolve(dataUrl);
  img.src = dataUrl;
});

// Horizontal double-bordered green "FULLY PAID" stamp.
const drawFullyPaidStamp = (doc, cx, cy) => {
  doc.saveGraphicsState();
  doc.setGState(new doc.GState({ opacity: 0.9 }));
  const w = 80, h = 26;
  doc.setDrawColor(...EMERALD_DARK);
  doc.setLineWidth(1.6);
  doc.rect(cx - w / 2, cy - h / 2, w, h, 'S');
  doc.setLineWidth(0.5);
  doc.rect(cx - w / 2 + 2.5, cy - h / 2 + 2.5, w - 5, h - 5, 'S');
  doc.setTextColor(...EMERALD_DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('FULLY PAID', cx, cy - 2, { align: 'center', baseline: 'middle' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('PAID IN FULL', cx, cy + 7, { align: 'center', baseline: 'middle' });
  doc.restoreGraphicsState();
};

export const exportWorkerTreePdf = async ({ worker, visa, supplier, tickets = [] }) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 15;

  // ────────────────── HEADER ──────────────────
  doc.setFillColor(...TADABER_NAVY);
  doc.rect(0, 0, pageW, 22, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('TADABER WORKFORCE', margin, 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('Worker Profile · Financial Report', margin, 18);
  doc.setFontSize(8);
  const issued = `Issued: ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`;
  doc.text(issued, pageW - margin, 18, { align: 'right' });

  let y = 32;

  // ────────────────── PHOTO + IDENTITY ──────────────────
  const photoSize = 38;
  let photo = normalizePhoto(worker.photoData || worker.workerPhoto);
  if (photo) {
    try {
      photo = await upscalePhoto(photo, 800);
      doc.addImage(photo, 'JPEG', margin, y, photoSize, photoSize, undefined, 'NONE');
    } catch (_) {
      doc.setFillColor(...SLATE_500);
      doc.roundedRect(margin, y, photoSize, photoSize, 2, 2, 'F');
    }
  } else {
    doc.setFillColor(...SLATE_500);
    doc.roundedRect(margin, y, photoSize, photoSize, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.text('NO PHOTO', margin + photoSize / 2, y + photoSize / 2, { align: 'center', baseline: 'middle' });
  }
  doc.setDrawColor(...TADABER_NAVY);
  doc.setLineWidth(0.4);
  doc.roundedRect(margin, y, photoSize, photoSize, 2, 2, 'S');

  const idX = margin + photoSize + 6;
  doc.setTextColor(...SLATE_900);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(String(worker.workerName || '—'), idX, y + 6);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...SLATE_700);
  doc.text(`Passport · ${worker.passportNumber || '—'}`, idX, y + 14);
  doc.text(`Nationality · ${worker.nationality || '—'}`, idX, y + 20);

  // status pill
  const statusMap = {
    arrived: { label: 'ARRIVED', color: EMERALD },
    abroad: { label: 'ABROAD', color: [59, 130, 246] },
    settled: { label: 'SETTLED', color: [168, 85, 247] },
    runaway: { label: 'RUNAWAY', color: RED },
    pending: { label: 'PENDING', color: [245, 158, 11] },
  };
  const st = statusMap[worker.status] || { label: String(worker.status || 'N/A').toUpperCase(), color: SLATE_500 };
  doc.setFillColor(...st.color);
  const pillW = doc.getTextWidth(st.label) + 6;
  doc.roundedRect(idX, y + 24, pillW, 6, 1.5, 1.5, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(st.label, idX + pillW / 2, y + 28, { align: 'center', baseline: 'middle' });

  // sponsor / supplier on the far right
  if (supplier?.name) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...SLATE_500);
    doc.text('LINKED SUPPLIER', pageW - margin, y + 14, { align: 'right' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...SLATE_900);
    doc.text(String(supplier.name).slice(0, 32), pageW - margin, y + 20, { align: 'right' });
    if (supplier.country) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...SLATE_500);
      doc.text(String(supplier.country), pageW - margin, y + 26, { align: 'right' });
    }
  }

  y += photoSize + 8;

  // divider
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.2);
  doc.line(margin, y, pageW - margin, y);
  y += 6;

  // ────────────────── FINANCIAL SUMMARY ──────────────────
  const totalCost = Number(worker.totalCost || worker.contractValue || 0);
  const paid = Number(worker.paidAmount || 0);
  const remaining = Number(worker.remainingAmount ?? Math.max(0, totalCost - paid));
  const deductions = Number(worker.deductionsUSD || 0);
  const isFullyPaid = totalCost > 0 && remaining <= 0;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...TADABER_NAVY);
  doc.text('FINANCIAL SUMMARY', margin, y);
  y += 6;

  // 4 metric cards
  const cardW = (pageW - margin * 2 - 9) / 4;
  const cardH = 22;
  const cards = [
    { label: 'TOTAL', value: fmtMoney(totalCost), color: SLATE_700 },
    { label: 'PAID', value: fmtMoney(paid), color: EMERALD },
    { label: 'REMAINING', value: fmtMoney(remaining), color: TADABER_NAVY },
    { label: 'DEDUCTIONS', value: fmtMoney(deductions), color: RED },
  ];
  cards.forEach((card, i) => {
    const x = margin + i * (cardW + 3);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(x, y, cardW, cardH, 2, 2, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.2);
    doc.roundedRect(x, y, cardW, cardH, 2, 2, 'S');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...SLATE_500);
    doc.text(card.label, x + 3, y + 6);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...card.color);
    doc.text(String(card.value), x + 3, y + 16);
  });

  y += cardH + 8;

  // ────────────────── CONTRACT INFO ──────────────────
  if (worker.contract_number || visa?.visa_number) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...SLATE_900);
    doc.text('CONTRACT & VISA', margin, y);
    y += 5;
    autoTable(doc, {
      startY: y,
      theme: 'plain',
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 1.5, textColor: SLATE_700 },
      columnStyles: {
        0: { fontStyle: 'bold', textColor: SLATE_500, cellWidth: 40 },
        1: { textColor: SLATE_900 },
      },
      body: [
        ['Contract #', String(worker.contract_number || '—')],
        ['Contract Date', fmtDate(worker.contractDate || worker.createdAt)],
        ['Visa Number', String(visa?.visa_number || '—')],
        ['Visa Expiry', fmtDate(visa?.expiry_date)],
        ['Sponsor', String(visa?.sponsor_name || '—')],
        ['Profession', String(visa?.profession || '—')],
      ].filter((r) => r[1] !== '—' || ['Contract #', 'Visa Number'].includes(r[0])),
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // ────────────────── PAYMENT LOG ──────────────────
  const log = (worker.paymentLog || worker.payments || []).slice().sort(
    (a, b) => (b.timestamp || b.date || '').localeCompare(a.timestamp || a.date || '')
  );

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...SLATE_900);
  doc.text(`PAYMENT LOG · ${log.length} entr${log.length === 1 ? 'y' : 'ies'}`, margin, y);
  y += 4;

  if (log.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(...SLATE_500);
    doc.text('No payments recorded yet.', margin, y + 5);
    y += 10;
  } else {
    autoTable(doc, {
      startY: y,
      head: [['#', 'Date', 'Amount', 'Description', 'By']],
      body: log.map((p, i) => [
        String(i + 1),
        fmtDate(p.date || p.timestamp),
        fmtMoney(p.amount),
        String(p.description || '—'),
        String(p.by || '—'),
      ]),
      margin: { left: margin, right: margin },
      headStyles: {
        fillColor: TADABER_NAVY,
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9,
        halign: 'left',
      },
      bodyStyles: { fontSize: 9, textColor: SLATE_900, cellPadding: 2 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: 8, halign: 'center', textColor: SLATE_500 },
        1: { cellWidth: 28 },
        2: { cellWidth: 30, fontStyle: 'bold', textColor: EMERALD },
        3: { cellWidth: 'auto' },
        4: { cellWidth: 22, textColor: SLATE_500 },
      },
      didDrawPage: () => {
        const ph = doc.internal.pageSize.getHeight();
        doc.setFontSize(7.5);
        doc.setTextColor(...SLATE_500);
        doc.text('TADABER Workforce · Confidential', margin, ph - 6);
        doc.text(
          `Page ${doc.internal.getNumberOfPages()}`,
          pageW - margin, ph - 6, { align: 'right' }
        );
      },
    });
    y = doc.lastAutoTable.finalY + 4;
  }

  if (log.length === 0) {
    const ph = doc.internal.pageSize.getHeight();
    doc.setFontSize(7.5);
    doc.setTextColor(...SLATE_500);
    doc.text('TADABER Workforce · Confidential', margin, ph - 6);
    doc.text('Page 1', pageW - margin, ph - 6, { align: 'right' });
  }

  // ────────────────── FULLY PAID stamp — vertical centre of empty white space ──────────────────
  if (isFullyPaid) {
    const totalPages = doc.internal.getNumberOfPages();
    doc.setPage(totalPages);
    const ph = doc.internal.pageSize.getHeight();
    const STAMP_VSPACE = 30;
    const FOOTER_TOP = ph - 12;
    const CONTENT_END = y + 4;
    let stampCy = (CONTENT_END + FOOTER_TOP) / 2;

    if (FOOTER_TOP - CONTENT_END < STAMP_VSPACE) {
      doc.addPage();
      stampCy = ph / 2;
    }
    drawFullyPaidStamp(doc, pageW / 2, stampCy);
  }

  const safeName = String(worker.workerName || 'worker').replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 60);
  doc.save(`TADABER_${safeName}_${worker.passportNumber || 'profile'}.pdf`);
};
